import Model from './Ova.jsx'
import { useRef, useMemo, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js'
import * as THREE from 'three'
import { dragState } from './dragState.js'
import { useThree } from '@react-three/fiber'
import { QUALITY, useRenderPath } from './device.js'

// Module-level mouse world position + velocity — shared by all StormLines, zero-alloc
const mouseWorld = new THREE.Vector3();
const mouseVel   = new THREE.Vector3(); // world-space delta per frame
const _unproj    = new THREE.Vector3();
const _prevMouse = new THREE.Vector3();
let   _mouseNdcX = 0; // raw NDC x (-1..1) for rotation bias

// Invisible influence radius around cursor (local space of SparkStorm group)
const INFLUENCE_R     = 1.4;  // world-local units — how far from cursor particles react

// Smoothed mouse speed (world units / frame) — updated in MouseTracker
let _mouseSpeed = 0;

// Transform mouse world position into the SparkStorm group's local space so
// the influence test stays correct as the group rotates.
const _mouseLocal     = new THREE.Vector3();
const _groupInvMatrix = new THREE.Matrix4();

// Tracks mouse world position and velocity every frame
function MouseTracker() {
    const { camera } = useThree();
    useFrame(({ mouse }) => {
        _mouseNdcX = mouse.x;
        _unproj.set(mouse.x, mouse.y, 0.5).unproject(camera);
        const dir = _unproj.sub(camera.position).normalize();
        if (Math.abs(dir.z) > 0.0001) {
            const t  = -camera.position.z / dir.z;
            const nx = camera.position.x + dir.x * t;
            const ny = camera.position.y + dir.y * t;
            mouseVel.set(nx - mouseWorld.x, ny - mouseWorld.y, 0);
            // Only mark moved if the cursor actually changed position
            if (!_mouseHasMoved && (Math.abs(nx) > 0.01 || Math.abs(ny) > 0.01)) {
                _mouseHasMoved = true;
            }
            mouseWorld.set(nx, ny, 0);
        }
        const rawSpeed = Math.sqrt(mouseVel.x * mouseVel.x + mouseVel.y * mouseVel.y);
        _mouseSpeed += (rawSpeed - _mouseSpeed) * 0.18;
    });
    return null;
}

// ─── Why the fallback rig looks the way it does ───────────────────────────────
// The rectAreaLight rig works because the two big panels ARE what the metal
// reflects. A directionalLight cannot stand in for that: a metal lit only by
// punctual lights is black apart from a pinpoint highlight, and a metal's F0 is
// its own base colour — at rgb(54,54,54) that is a ~5% reflector, so there is
// almost no diffuse term left to see either.
//
// An image-based environment would reproduce it faithfully, and an earlier attempt
// here built one with PMREMGenerator. That was wrong: PMREM renders into half-float
// targets, which are only renderable with EXT_color_buffer_half_float or
// EXT_color_buffer_float — NOT core WebGL2. On a phone without those the env map
// comes out black, and a metal reflecting a black environment is a black model,
// with no error raised anywhere. (The check that matters is renderability; the
// float-LINEAR extension is a different thing and does not imply it.)
//
// So the fallback drops reflection-dependence entirely: much lower metalness, a
// lighter base colour, and a lifted ambient so the diffuse term carries the shape.
// Less of a polished-metal read than the desktop path, but it cannot come out
// black on any driver.

function SunLight() {
    const { rectAreaLights } = useRenderPath();
    const warmRef = useRef();
    const coolRef = useRef();
    const ambientRef = useRef();
    const angleRef = useRef(0);
    const warmColor = new THREE.Color('#ffd580');
    const coolColor = new THREE.Color('#2af5c8');

    // Uploads the LTC lookup DataTextures. Only the rectAreaLight path needs them,
    // and it must happen before the first render of such a light — doing it here
    // keeps the cost off every device that takes the directional rig.
    if (rectAreaLights) RectAreaLightUniformsLib.init();

    // Without the broad reflected fill of the area lights, the ambient term carries
    // much more of the model's visibility.
    const ambientScale = rectAreaLights ? 1.0 : 1.5;

    useFrame((state, delta) => {
        angleRef.current += delta * 0.125;

        const radius = 8;
        const tilt = Math.PI * 0.7;

        // Warm orange key light
        const wx = Math.cos(angleRef.current) * radius;
        const wzFlat = Math.sin(angleRef.current) * radius;
        const wy = wzFlat * Math.sin(tilt) + 2;
        const wz = wzFlat * Math.cos(tilt);

        // Cool blue fill — opposite side, slightly lower
        const cx = Math.cos(angleRef.current + Math.PI) * radius;
        const czFlat = Math.sin(angleRef.current + Math.PI) * radius;
        const cy = czFlat * Math.sin(tilt) - 1;
        const cz = czFlat * Math.cos(tilt);

        if (warmRef.current) {
            warmRef.current.position.set(wx, wy, wz);
            warmRef.current.lookAt(0, 0, 0);
            _galaxyWarmPos.set(wx, wy, wz);
        }
        if (coolRef.current) {
            coolRef.current.position.set(cx, cy, cz);
            coolRef.current.lookAt(0, 0, 0);
            _galaxyCoolPos.set(cx, cy, cz);
        }

        // Ambient pulses between warm/cool tones as lights orbit
        if (ambientRef.current) {
            // t goes 0→1→0 as warm key sweeps front-to-back
            const t = (Math.sin(angleRef.current) * 0.5 + 0.5);
            ambientRef.current.intensity = (0.12 + t * 0.18) * ambientScale;
            ambientRef.current.color.lerpColors(coolColor, warmColor, t);
        }
    });

    // Fallback rig: directional lights on the same orbit as the area lights.
    // A DirectionalLight aims from its position toward light.target, which defaults
    // to an Object3D at the origin whose matrixWorld stays identity — so the
    // per-frame position.set already points it at the model, and the lookAt in
    // useFrame is a harmless no-op for this light type.
    if (!rectAreaLights) {
        return (
            <>
                {/* Golden sunlight key */}
                <directionalLight ref={warmRef} intensity={1.8} color="#fdf4eb" />
                {/* Atmospheric teal scatter — dark side */}
                <directionalLight ref={coolRef} intensity={0.95} color="#d7f0fc" />
                {/* Animated ambient — pulses warm/cool with the orbit */}
                <ambientLight ref={ambientRef} intensity={0.2 * ambientScale} />
            </>
        );
    }

    return (
        <>
            {/* Golden sunlight key */}
            <rectAreaLight
                ref={warmRef}
                intensity={2}
                width={22}
                height={18}
                color="#fdf4eb"
            />
            {/* Atmospheric teal scatter — dark side */}
            <rectAreaLight
                ref={coolRef}
                intensity={1}
                width={10}
                height={18}
                color="#d7f0fc"
            />
            {/* Animated ambient — pulses warm/cool with the orbit */}
            <ambientLight ref={ambientRef} intensity={0.2} />
        </>
    );
}

// ─── Output encoding for the custom particle shaders ─────────────────────────
// three.js only appends <colorspace_fragment> to its OWN material shaders — a
// custom ShaderMaterial writes whatever it assigns to gl_FragColor straight into
// the framebuffer. On the motion-blur path that is fine, because the final blit
// applies linearToSRGB to the whole frame. Without that pass the same values go
// to the canvas unencoded and the particle fields read markedly darker.
//
// So when the blit is gone, the shaders encode their own output and both paths
// land in the same place. Under additive blending, encoding per fragment is not
// identical to encoding the accumulated sum — it matches exactly where particles
// do not overlap, and runs slightly hot in dense cores. Close enough, and far
// closer than leaving the field dim.
//
// A function rather than a constant because whether the blit runs is only known
// once the real renderer exists, so the shader source is assembled per material.
const outEncode = (motionBlur) => motionBlur
    ? /* glsl */`vec3 encodeOutput(vec3 c) { return c; }`
    : /* glsl */`
  vec3 encodeOutput(vec3 c) {
    return mix(c * 12.92,
               1.055 * pow(clamp(c, 0.0, 1.0), vec3(1.0 / 2.2)) - 0.055,
               step(vec3(0.0031308), c));
  }`;

const STAR_VERT = /* glsl */`
  attribute float aSize;
  attribute float aBrightness;
  attribute float aPhase;
  attribute vec3  aColor;
  uniform   float uTime;
  uniform   float uDpr;
  varying   float vAlpha;
  varying   vec3  vColor;
  void main() {
    float twinkle = 1.0;
    vAlpha  = aBrightness * twinkle;
    vColor  = aColor;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    // Flat pixel size — no depth division.
    // Depth-scaled sizing caused size to oscillate as the group moved in Z
    // each frame, which was the direct cause of the blinking.
    // gl_PointSize is in FRAMEBUFFER pixels, so multiplying by the pixel ratio is
    // what keeps a star the same apparent size on a dense display instead of
    // shrinking to a sub-pixel speck.
    gl_PointSize = max(aSize * 2.5, 1.5) * uDpr;
    gl_Position  = projectionMatrix * mv;
  }
`;
const starFrag = (motionBlur) => /* glsl */`
  precision mediump float;
  varying float vAlpha;
  varying vec3  vColor;
  ${outEncode(motionBlur)}
  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    float soft = 1.0 - smoothstep(0.0, 0.5, d);
    gl_FragColor = vec4(encodeOutput(vColor), soft * soft * vAlpha);
  }
`;

function StarField({ count = 8000, spread = 120 }) {
    const { motionBlur } = useRenderPath();
    const groupRef = useRef();
    const matRef   = useRef();

    const { positions, sizes, brightnesses, phases, colors } = useMemo(() => {
        const positions    = new Float32Array(count * 3);
        const sizes        = new Float32Array(count);
        const brightnesses = new Float32Array(count);
        const phases       = new Float32Array(count);
        const colors       = new Float32Array(count * 3);

        // Pre-generate cluster centers on the sphere (Milky Way-like band + random patches)
        const clusterCount = 18;
        const clusterCenters = new Float32Array(clusterCount * 2);
        for (let c = 0; c < clusterCount; c++) {
            // Bias clusters toward a band (galactic plane feel)
            const bandBias = Math.random() < 0.55;
            clusterCenters[c*2]   = Math.random() * Math.PI * 2;
            clusterCenters[c*2+1] = bandBias
                ? Math.PI * 0.5 + (Math.random() - 0.5) * 0.6  // equatorial band
                : Math.acos(2 * Math.random() - 1);             // random position
        }

        // Star color palette: cool white, blue-white, faint warm
        const palette = [
            [0.85, 0.92, 1.00],
            [0.75, 0.88, 1.00],
            [1.00, 0.97, 0.90],
            [0.90, 0.95, 1.00],
            [0.70, 0.80, 1.00],
        ];

        for (let i = 0; i < count; i++) {
            let px, py, pz;
            let inCluster = false;
            // 60% stars in clusters, 40% uniform field
            if (Math.random() < 0.6) {
                inCluster = true;
                // Pick a random cluster center on the sphere
                const ci     = Math.floor(Math.random() * clusterCount);
                const cTheta = clusterCenters[ci * 2];
                const cPhi   = clusterCenters[ci * 2 + 1];
                // Gaussian scatter around cluster center
                const scatter = 0.18 + Math.random() * 0.28;
                const dTheta  = (Math.random() + Math.random() - 1.0) * scatter;
                const dPhi    = (Math.random() + Math.random() - 1.0) * scatter;
                const theta   = cTheta + dTheta;
                const phi     = Math.max(0.01, Math.min(Math.PI - 0.01, cPhi + dPhi));
                const r       = spread * (0.82 + Math.random() * 0.18);
                px = r * Math.sin(phi) * Math.cos(theta);
                py = r * Math.sin(phi) * Math.sin(theta);
                pz = r * Math.cos(phi);
            } else {
                const theta = Math.random() * Math.PI * 2;
                const phi   = Math.acos(2 * Math.random() - 1);
                const r     = spread * (0.8 + Math.random() * 0.2);
                px = r * Math.sin(phi) * Math.cos(theta);
                py = r * Math.sin(phi) * Math.sin(theta);
                pz = r * Math.cos(phi);
            }
            positions[i*3] = px; positions[i*3+1] = py; positions[i*3+2] = pz;

            const u = Math.random();
            if (inCluster) {
                // Cluster stars: small and dim — like a dense far-away cluster
                sizes[i]        = 0.08 + Math.pow(u, 4.0) * 0.60;
                brightnesses[i] = 0.20 + Math.pow(Math.random(), 2.2) * 0.50;
            } else {
                // Field stars: power-law, a few much bigger/brighter
                sizes[i]        = 0.12 + Math.pow(u, 3.0) * 2.0;
                brightnesses[i] = 0.30 + Math.pow(Math.random(), 1.8) * 0.70;
            }
            phases[i]       = Math.random();

            const col = palette[Math.floor(Math.random() * palette.length)];
            // Slight per-star tint variation
            colors[i*3]   = col[0] * (0.9 + Math.random() * 0.1);
            colors[i*3+1] = col[1] * (0.9 + Math.random() * 0.1);
            colors[i*3+2] = col[2] * (0.9 + Math.random() * 0.1);
        }
        return { positions, sizes, brightnesses, phases, colors };
    }, [count, spread]);

    const material = useMemo(() => new THREE.ShaderMaterial({
        vertexShader:   STAR_VERT,
        fragmentShader: starFrag(motionBlur),
        uniforms: { uTime: { value: 0 }, uDpr: { value: 1 } },
        transparent:  true,
        depthWrite:   false,
        depthTest:    true,
        blending:     THREE.AdditiveBlending,
    }), [motionBlur]);

    // Very slow Y rotation — safe because:
    // 1. Flat point sizes don't change with movement (no size oscillation)
    // 2. Temporal blur only activates during drag; normal view is a straight blit
    useFrame((state) => {
        material.uniforms.uDpr.value = state.gl.getPixelRatio();
        if (!groupRef.current) return;
        groupRef.current.rotation.y = state.clock.elapsedTime * 0.00015;
    });

    return (
        <group ref={groupRef}>
        <points material={material}>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position"    count={count} array={positions}    itemSize={3} />
                <bufferAttribute attach="attributes-aSize"       count={count} array={sizes}         itemSize={1} />
                <bufferAttribute attach="attributes-aBrightness" count={count} array={brightnesses}  itemSize={1} />
                <bufferAttribute attach="attributes-aPhase"      count={count} array={phases}        itemSize={1} />
                <bufferAttribute attach="attributes-aColor"      count={count} array={colors}        itemSize={3} />
            </bufferGeometry>
        </points>
        </group>
    );
}

// ─── Bird swarm — boids on a sphere shell ────────────────────────────────────

const BIRD_N      = 9;
const BIRD_TRAIL  = 20;  // short sentinel trails
const BIRD_R      = 1.55;

const B_SPEED   = 0.007;  // base orbital speed (rad/frame)
const B_MAXV    = 0.016;  // max total angular speed
const B_SEP_R   = 0.44;   // separation radius
const B_SEP_F   = 0.011;  // separation weight
const B_ALI_F   = 0.032;  // alignment — steers toward neighbour heading
// No cohesion — it pulls birds into tight clusters whose trails form visible loops
const B_ALI_R   = 1.3;    // neighbourhood radius

const BIRD_COLORS = ['#ffffff', '#a8d8ff', '#5bc8ff', '#c8a8ff', '#a0ffe0'];
// Pre-parse base colours as linear RGB for per-frame light modulation
const _bColorR = new Float32Array(BIRD_N);
const _bColorG = new Float32Array(BIRD_N);
const _bColorB = new Float32Array(BIRD_N);
;(function parseColors() {
    const tmp = new THREE.Color();
    for (let i = 0; i < BIRD_N; i++) {
        tmp.set(BIRD_COLORS[i % BIRD_COLORS.length]);
        _bColorR[i] = tmp.r; _bColorG[i] = tmp.g; _bColorB[i] = tmp.b;
    }
})();

// ── Module-level flat state — zero allocations each frame ─────────────────────
const _bPhi    = new Float32Array(BIRD_N);
const _bTheta  = new Float32Array(BIRD_N);
const _bVphi   = new Float32Array(BIRD_N);
const _bVtheta = new Float32Array(BIRD_N);
const _bTrails = Array.from({ length: BIRD_N }, () => new Float32Array(BIRD_TRAIL * 3));
const _bVis    = new Float32Array(BIRD_N).fill(1);
// temp arrays — written during force phase, applied in integration phase
const _nVphi   = new Float32Array(BIRD_N);
const _nVtheta = new Float32Array(BIRD_N);
// last world-space head, used to detect jumps and flush trail
const _bHeadX  = new Float32Array(BIRD_N);
const _bHeadY  = new Float32Array(BIRD_N);
const _bHeadZ  = new Float32Array(BIRD_N);
const _bInited = new Uint8Array(BIRD_N); // 0 until first real frame
// ~40% of birds stay visible on hover; rest fade out — gives sparse sentinel look
const _bHoverShow = new Uint8Array(BIRD_N);
;(function assignHoverBirds() {
    // Randomly pick ~40% to remain visible when just hovering
    for (let i = 0; i < BIRD_N; i++) _bHoverShow[i] = Math.random() < 0.4 ? 1 : 0;
})();
// per-bird radial state — real physics: each bird flies in/out dynamically
const _bR     = new Float32Array(BIRD_N);   // current actual radius from model
const _bVr    = new Float32Array(BIRD_N);   // radial velocity (positive = away)
const _bBaseR = new Float32Array(BIRD_N);   // slowly-wandering comfort radius
// per-bird mouse-push offset + velocity (world space)
const _bOx = new Float32Array(BIRD_N);
const _bOy = new Float32Array(BIRD_N);
const _bOz = new Float32Array(BIRD_N);
const _bVox = new Float32Array(BIRD_N);
const _bVoy = new Float32Array(BIRD_N);
const _bVoz = new Float32Array(BIRD_N);
let _bTime = 0;

const MOUSE_FADE_R  = 0.55;  // ~95px at fov50/z5 — tight local cursor zone

let _bRotSpd = 0, _bPrevY = 0, _bPrevX = 0;
let _bReadyPrev     = false;
let _bWasHovering   = false; // tracks hover state for flush-on-exit
let _bHovWasStopped = false;
let _bFadeIn        = 0;    // counts down; keeps opacity 0 briefly on first frame
const FADE_IN_HOLD  = 8;   // just a couple frames to let trail buffers seed cleanly
let _mouseHasMoved  = false; // true once MouseTracker has actually updated mouseWorld

// One-time initialisation on module load
;(function initBoids() {
    const ga = Math.PI * (3 - Math.sqrt(5)); // golden angle
    for (let i = 0; i < BIRD_N; i++) {
        _bPhi[i]    = Math.acos(1 - 2 * (i + 0.5) / BIRD_N);
        _bTheta[i]  = ga * i;
        _bVphi[i]   = (Math.random() - 0.5) * 0.003;
        _bVtheta[i] = B_SPEED * (0.6 + Math.random() * 0.8);
        // wide depth spread — some skim the model, some orbit far out
        _bBaseR[i] = 0.75 + Math.random() * 1.65;  // comfort radius 0.75–2.4
        _bR[i]     = _bBaseR[i] + (Math.random() - 0.5) * 0.5;
        _bVr[i]    = (Math.random() - 0.5) * 0.006;
    }
    // pre-fill trail buffers so no initial flash
    for (let i = 0; i < BIRD_N; i++) {
        const sp = Math.sin(_bPhi[i]), cp = Math.cos(_bPhi[i]);
        const st = Math.sin(_bTheta[i]), ct = Math.cos(_bTheta[i]);
        const x  = _bR[i] * sp * ct, y = _bR[i] * cp, z = _bR[i] * sp * st;
        for (let j = 0; j < BIRD_TRAIL; j++) {
            _bTrails[i][j*3] = x; _bTrails[i][j*3+1] = y; _bTrails[i][j*3+2] = z;
        }
    }
})();

function Swarm() {
    const geoRefs = useRef([]);
    const matRefs = useRef([]);

    useFrame((_, delta) => {
        // ── Don't run until Ova has written a real rotY ─────────────────────
        if (!dragState.modelReady) return;

        const dt = Math.min(delta, 1/30);

        // ── Rotation speed tracking ──────────────────────────────────────────
        const dY = dragState.rotY - _bPrevY;
        const dX = dragState.rotX - _bPrevX;
        _bPrevY = dragState.rotY; _bPrevX = dragState.rotX;
        const rotDelta = Math.sqrt(dY*dY + dX*dX);
        _bRotSpd += (rotDelta - _bRotSpd) * 0.15;
        _bTime += dt;

        // On the exact frame modelReady becomes true (or first frame here),
        // flush all trail buffers to current real positions before doing anything.
        const justReady = !_bReadyPrev;
        _bReadyPrev = true;
        if (justReady) _bFadeIn = FADE_IN_HOLD;

        // Hover state: hovering without active drag
        const isHovering = dragState.hovering; // true for both hover-only and active drag
        const hovStopped = isHovering && _bRotSpd < 0.0003;
        // justUnhovered only used for trail flush — no longer sets _bFadeIn
        // (setDrawRange makes connections impossible so no blank period needed)
        const justUnhovered = _bWasHovering && !dragState.hovering;
        _bWasHovering   = dragState.hovering;
        _bHovWasStopped = hovStopped;
        if (_bFadeIn > 0) _bFadeIn--;

        // ── Boid forces (O(N²), N = 90 → ~8100 ops) ────────────────────────
        for (let i = 0; i < BIRD_N; i++) {
            let sPhi=0, sTheta=0;          // separation
            let aPhi=0, aTheta=0, aN=0;    // alignment

            for (let j = 0; j < BIRD_N; j++) {
                if (i === j) continue;
                const dp = _bPhi[i]   - _bPhi[j];
                let   dt2 = _bTheta[i] - _bTheta[j];
                if (dt2 >  Math.PI) dt2 -= Math.PI * 2;
                if (dt2 < -Math.PI) dt2 += Math.PI * 2;
                const d = Math.sqrt(dp*dp + dt2*dt2);
                if (d < 0.001) continue;

                if (d < B_SEP_R) {
                    const w = (B_SEP_R - d) / B_SEP_R;
                    sPhi   += (dp / d) * w;
                    sTheta += (dt2 / d) * w;
                }
                if (d < B_ALI_R) {
                    aPhi   += _bVphi[j]; aTheta += _bVtheta[j]; aN++;
                }
            }

            let vp = _bVphi[i], vt = _bVtheta[i];

            vp += sPhi   * B_SEP_F;
            vt += sTheta * B_SEP_F;

            if (aN > 0) {
                vp += ((aPhi/aN) - vp) * B_ALI_F;
                vt += ((aTheta/aN) - vt) * B_ALI_F;
            }

            // Pole avoidance — gentle push away from poles, not toward equator
            const distFromPole = Math.min(_bPhi[i], Math.PI - _bPhi[i]);
            if (distFromPole < 0.4) {
                vp += (_bPhi[i] < Math.PI / 2 ? 1 : -1) * (0.4 - distFromPole) * 0.006;
            }

            // base orbital speed
            vt += (B_SPEED - vt) * 0.006;
            // insect angular jitter — subtle erratic movement, not too jagged
            vp += (Math.random() - 0.5) * 0.002;
            vt += (Math.random() - 0.5) * 0.0015;

            const spd = Math.sqrt(vp*vp + vt*vt);
            if (spd > B_MAXV) { vp = (vp/spd)*B_MAXV; vt = (vt/spd)*B_MAXV; }

            _nVphi[i] = vp; _nVtheta[i] = vt;
        }

        // ── Integration + world transform + trail update ─────────────────────
        const cy = Math.cos(dragState.rotY), sy = Math.sin(dragState.rotY);
        const cx = Math.cos(dragState.rotX), sx = Math.sin(dragState.rotX);

        for (let i = 0; i < BIRD_N; i++) {
            // Slow boid own-velocity during hover — birds drift lazily, don't stop
            const hoverMult = isHovering ? 0.0 : 1.0;
            const spinDamp = Math.max(0.0, 1.0 - _bRotSpd * 40);
            _bVphi[i]   = _nVphi[i]   * spinDamp * hoverMult;
            _bVtheta[i] = _nVtheta[i] * spinDamp * hoverMult + B_SPEED * (1.0 - spinDamp) * hoverMult;
            _bPhi[i]    = Math.max(0.08, Math.min(Math.PI - 0.08, _bPhi[i] + _bVphi[i]));
            _bTheta[i] += _bVtheta[i];

            const sp = Math.sin(_bPhi[i]), cp = Math.cos(_bPhi[i]);
            const st = Math.sin(_bTheta[i]), ct = Math.cos(_bTheta[i]);
            // insect-around-light radial dynamics:
            //   spring pull toward own comfort radius + random jitter + rare dart inward
            _bBaseR[i] += (Math.random() - 0.5) * 0.0015;   // comfort radius slowly wanders
            _bBaseR[i]  = Math.max(0.72, Math.min(2.45, _bBaseR[i]));
            _bVr[i] += (_bBaseR[i] - _bR[i]) * 0.018;       // spring toward comfort
            _bVr[i] += (Math.random() - 0.5) * 0.005;        // random radial jitter
            if (Math.random() < 0.003) _bVr[i] -= 0.03;      // rare dart toward model
            _bVr[i] *= 0.88;                                   // damping
            _bR[i]   = Math.max(0.58, Math.min(2.5, _bR[i] + _bVr[i]));
            const r  = _bR[i];
            const lx = r * sp * ct, ly = r * cp, lz = r * sp * st;

            // local → world (Y then X rotation)
            const rx  = lx*cy + lz*sy;
            const wy0 = ly;
            const rz  = -lx*sy + lz*cy;
            const wxBase = rx, wyBase = wy0*cx - rz*sx, wzBase = wy0*sx + rz*cx;

            // Mouse repulsion — same spring system as galaxy particles
            if (_mouseHasMoved) {
                const BPUSH_R = 1.8;
                const BPUSH_F = 0.055;
                const BSPRING = 0.028;
                const BDAMP   = 0.84;
                const ex = (wxBase + _bOx[i]) - mouseWorld.x;
                const ey = (wyBase + _bOy[i]) - mouseWorld.y;
                const ez = (wzBase + _bOz[i]) - mouseWorld.z;
                const dist = Math.sqrt(ex*ex + ey*ey + ez*ez);
                if (dist < BPUSH_R && dist > 0.001) {
                    const inv = (1 - dist / BPUSH_R) * BPUSH_F / dist;
                    _bVox[i] += ex * inv;
                    _bVoy[i] += ey * inv;
                    _bVoz[i] += ez * inv;
                }
                _bVox[i] += -_bOx[i] * BSPRING;
                _bVoy[i] += -_bOy[i] * BSPRING;
                _bVoz[i] += -_bOz[i] * BSPRING;
                _bVox[i] *= BDAMP; _bVoy[i] *= BDAMP; _bVoz[i] *= BDAMP;
                _bOx[i]  += _bVox[i]; _bOy[i] += _bVoy[i]; _bOz[i] += _bVoz[i];
            }
            const wx = wxBase + _bOx[i];
            const wy = wyBase + _bOy[i];
            const wz = wzBase + _bOz[i];

            // trail visibility: on hover show short trails; otherwise full length
            const targetLen = isHovering ? 4 : BIRD_TRAIL;
            _bVis[i] += (targetLen - _bVis[i]) * (isHovering ? 0.006 : 0.10);
            const vl   = Math.max(1, Math.round(_bVis[i]));
            const tail = BIRD_TRAIL - vl;

            const tr = _bTrails[i];

            // Only flush on init — setDrawRange masks old positions so no connection possible
            const needFlush = justReady || justUnhovered || !_bInited[i];
            const resetVis  = justReady || justUnhovered || !_bInited[i];

            if (needFlush) {
                for (let k = 0; k < BIRD_TRAIL; k++) {
                    tr[k*3]=wx; tr[k*3+1]=wy; tr[k*3+2]=wz;
                }
                if (resetVis) _bVis[i] = 1;
                _bInited[i] = 1;
            } else {
                // Shift buffer: oldest at 0, newest at end
                for (let k = 0; k < BIRD_TRAIL - 1; k++) {
                    tr[k*3]=tr[(k+1)*3]; tr[k*3+1]=tr[(k+1)*3+1]; tr[k*3+2]=tr[(k+1)*3+2];
                }
                tr[(BIRD_TRAIL-1)*3]=wx; tr[(BIRD_TRAIL-1)*3+1]=wy; tr[(BIRD_TRAIL-1)*3+2]=wz;
                // NO tail-fill — drawRange renders only [tail .. BIRD_TRAIL-1]
            }
            _bHeadX[i]=wx; _bHeadY[i]=wy; _bHeadZ[i]=wz;

            // opacity — fade out near mouse cursor only when NOT hovering the model
            // (while hovering, mouseWorld points at the model center and would occlude everything)
            const mouseOcc = (_mouseHasMoved && MOUSE_FADE_R > 0 && !isHovering) ? (() => {
                const mdx = wx - mouseWorld.x, mdy = wy - mouseWorld.y;
                const mouseDist = Math.sqrt(mdx*mdx + mdy*mdy);
                return mouseDist < MOUSE_FADE_R ? 1.0 - (mouseDist / MOUSE_FADE_R) : 0.0;
            })() : 0.0;
            const mat = matRefs.current[i];
            if (mat) {
                // Hover: all birds visible but dimmer + short trails; full opacity when free
                const baseTgt = _bFadeIn > 0 ? 0.0
                              : isHovering   ? 0.10
                              : 0.22;
                const tgt = baseTgt * (1.0 - mouseOcc * 0.95);
                mat.opacity += (tgt - mat.opacity) * (tgt > mat.opacity ? 0.04 : 0.08);
                // Real orbiting lights — same positions as galaxy particles
                const wlen = Math.sqrt(wx*wx + wy*wy + wz*wz) || 1;
                const nx = wx/wlen, ny = wy/wlen, nz = wz/wlen;
                const wl = _galaxyWarmPos.length() || 1;
                const cl = _galaxyCoolPos.length() || 1;
                const ndotWarm = Math.max(0, nx*_galaxyWarmPos.x/wl + ny*_galaxyWarmPos.y/wl + nz*_galaxyWarmPos.z/wl);
                const ndotCool = Math.max(0, nx*_galaxyCoolPos.x/cl + ny*_galaxyCoolPos.y/cl + nz*_galaxyCoolPos.z/cl);
                const bright = ndotWarm * 0.90 + ndotCool * 0.25 + 0.05;
                mat.color.setRGB(
                    _bColorR[i] * bright,
                    _bColorG[i] * bright,
                    _bColorB[i] * bright
                );
            }
            const geo = geoRefs.current[i];
            if (geo) {
                // setDrawRange: only render the most recent vl points
                // old positions outside this range are never drawn — no connection possible
                geo.setDrawRange(tail, vl);
                geo.attributes.position.needsUpdate = true;
            }
        }
    });

    return (
        <>
            {Array.from({ length: BIRD_N }, (_, i) => (
                <line key={i}>
                    <bufferGeometry ref={el => { geoRefs.current[i] = el; }}>
                        <bufferAttribute attach="attributes-position"
                            count={BIRD_TRAIL} array={_bTrails[i]} itemSize={3} />
                    </bufferGeometry>
                    <lineBasicMaterial ref={el => { matRefs.current[i] = el; }}
                        color={BIRD_COLORS[i % BIRD_COLORS.length]}
                        transparent opacity={0.0}
                        blending={THREE.AdditiveBlending} depthWrite={false} />
                </line>
            ))}
        </>
    );
}

// ─── Galaxy particle field — 1500 drifting stars, per-vertex size + colour ─────────────
// Particles near the model co-rotate with it; outer ones barely feel it.


// Shared light positions — written by SunLight every frame, read by GalaxyField
const _galaxyWarmPos = new THREE.Vector3();
const _galaxyCoolPos = new THREE.Vector3();
const GALAXY_VERT = /* glsl */`
  attribute float size;
  attribute vec3  aColor;
  uniform   vec3  uWarmPos;
  uniform   vec3  uCoolPos;
  uniform   float uDpr;
  varying   vec3  vColor;
  varying   float vAlpha;
  void main() {
    vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    vec3 norm = normalize(worldPos);
    float ndotWarm = max(0.0, dot(norm, normalize(uWarmPos)));
    float ndotCool = max(0.0, dot(norm, normalize(uCoolPos)));
    float lit = ndotWarm * 0.90 + ndotCool * 0.25;
    vColor = aColor * lit;
    vAlpha = 0.04 + lit * 0.96;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    // Distance attenuation puts these well under one framebuffer pixel once the
    // camera pulls back, and GL then clamps them to a single pixel — which on a
    // dense phone display is a third of a CSS pixel and reads as "the particles
    // are gone". Working in CSS pixels (hence the uDpr factor) with a floor keeps
    // them the same apparent size on every device.
    gl_PointSize = max(size * (2.5 / -mv.z), 1.2) * uDpr;
    gl_Position  = projectionMatrix * mv;
  }
`;
const galaxyFrag = (motionBlur) => /* glsl */`
  varying vec3  vColor;
  varying float vAlpha;
  ${outEncode(motionBlur)}
  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    float soft  = 1.0 - smoothstep(0.3, 0.5, d);
    gl_FragColor = vec4(encodeOutput(vColor), soft * vAlpha);
  }
`;

const GALAXY_COLORS = [
  new THREE.Color('#CFD6DE').multiplyScalar(0.55),
  new THREE.Color('#1EE3CF').multiplyScalar(0.45),
  new THREE.Color('#6B48FF').multiplyScalar(0.50),
  new THREE.Color('#125D98').multiplyScalar(0.60),
];

function GalaxyField({ count = 3500 }) {
  const { motionBlur } = useRenderPath();
  const geoRef   = useRef();
  const groupRef = useRef();

  // Build per-star data once — mutable refs for the push/spring loop
  const stars = useMemo(() => Array.from({ length: count }, () => {
    const r     = 0.4 + Math.random() * 1.8;
    const phi   = Math.acos(1 - 2 * Math.random()); // uniform polar 0→π
    const theta = Math.random() * Math.PI * 2;       // azimuthal 0→2π
    const col = GALAXY_COLORS[Math.floor(Math.random() * GALAXY_COLORS.length)];
    return { r, phi, theta,
      size: Math.random() * 1.0 + 1.5,
      color: col.clone(),
      // Cartesian push offset + velocity — for mouse repulsion
      ox: 0, oy: 0, oz: 0,
      vox: 0, voy: 0, voz: 0 };
  }), [count]);

  // Flat typed arrays — reused every frame (zero-alloc after init).
  // The shell never drifts (phi/theta are fixed), so the spherical→cartesian
  // conversion is done once here instead of 3 sin + 2 cos per particle per frame.
  // positions starts as a copy of basePos so the very first frame is already
  // correct even if the integration loop is skipped.
  const { basePos, positions } = useMemo(() => {
    const basePos = new Float32Array(count * 3);
    stars.forEach((s, i) => {
      const sp = Math.sin(s.phi), cp = Math.cos(s.phi);
      basePos[i * 3]     = s.r * sp * Math.sin(s.theta);
      basePos[i * 3 + 1] = s.r * cp;
      basePos[i * 3 + 2] = s.r * sp * Math.cos(s.theta);
    });
    return { basePos, positions: basePos.slice() };
  }, [stars, count]);
  const colors    = useMemo(() => {
    const arr = new Float32Array(count * 3);
    stars.forEach((s, i) => {
      arr[i * 3]     = s.color.r;
      arr[i * 3 + 1] = s.color.g;
      arr[i * 3 + 2] = s.color.b;
    });
    return arr;
  }, [stars, count]);
  const sizes = useMemo(() => {
    const arr = new Float32Array(count);
    stars.forEach((s, i) => { arr[i] = s.size; });
    return arr;
  }, [stars, count]);

  const material = useMemo(() => new THREE.ShaderMaterial({
    vertexShader:   GALAXY_VERT,
    fragmentShader: galaxyFrag(motionBlur),
    uniforms: {
      uWarmPos: { value: new THREE.Vector3(0, 8, 0) },
      uCoolPos: { value: new THREE.Vector3(0, -1, -8) },
      uDpr:     { value: 1 },
    },
    blending:       THREE.AdditiveBlending,
    depthWrite:     false,
    depthTest:      true,
    transparent:    true,
  }), [motionBlur]);

  // True while any particle is still displaced — lets the whole integration loop
  // be skipped once the field has settled and the cursor is out of range.
  const restingRef = useRef(true);

  useFrame((state) => {
    if (!geoRef.current) return;

    // Sync light positions into shader uniforms every frame
    material.uniforms.uWarmPos.value.copy(_galaxyWarmPos);
    material.uniforms.uCoolPos.value.copy(_galaxyCoolPos);
    material.uniforms.uDpr.value = state.gl.getPixelRatio();

    // Mirror model rotation exactly
    if (groupRef.current) {
      groupRef.current.rotation.y = dragState.rotY;
      groupRef.current.rotation.x = dragState.rotX;
    }

    const PUSH_R = 1.6;   // influence radius (world units)
    const PUSH_F = 0.04;  // impulse strength
    const SHELL_MAX = 2.2 + PUSH_R; // outermost base radius plus reach

    // The cursor can only touch the shell when it is within reach of it — one
    // cheap test replaces `count` distance checks when the cursor is far away.
    const push = QUALITY.particlePush && _mouseHasMoved &&
      (mouseWorld.x * mouseWorld.x + mouseWorld.y * mouseWorld.y + mouseWorld.z * mouseWorld.z) < SHELL_MAX * SHELL_MAX;

    // Settled and nothing pushing — positions already hold the rest pose
    if (!push && restingRef.current) return;

    const EPS = 1e-5;
    let active = false;

    for (let i = 0; i < count; i++) {
      const s  = stars[i];
      const i3 = i * 3;
      const bx = basePos[i3], by = basePos[i3 + 1], bz = basePos[i3 + 2];

      // Mouse repulsion — push offset away from cursor in 3D
      if (push) {
        const ex = (bx + s.ox) - mouseWorld.x;
        const ey = (by + s.oy) - mouseWorld.y;
        const ez = (bz + s.oz) - mouseWorld.z;
        const dist = Math.sqrt(ex*ex + ey*ey + ez*ez);
        if (dist < PUSH_R && dist > 0.001) {
          const falloff = (1 - dist / PUSH_R);
          const inv = falloff * PUSH_F / dist;
          s.vox += ex * inv;
          s.voy += ey * inv;
          s.voz += ez * inv;
        }
      }

      // Spring back toward base position + damping
      s.vox += -s.ox * 0.035;  // spring
      s.voy += -s.oy * 0.035;
      s.voz += -s.oz * 0.035;
      s.vox *= 0.88;            // damping
      s.voy *= 0.88;
      s.voz *= 0.88;
      s.ox  += s.vox;
      s.oy  += s.voy;
      s.oz  += s.voz;

      // Snap imperceptible residuals to exactly zero so the field can go to rest
      if (Math.abs(s.ox) + Math.abs(s.oy) + Math.abs(s.oz) +
          Math.abs(s.vox) + Math.abs(s.voy) + Math.abs(s.voz) < EPS) {
        s.ox = s.oy = s.oz = s.vox = s.voy = s.voz = 0;
      } else {
        active = true;
      }

      positions[i3]     = bx + s.ox;
      positions[i3 + 1] = by + s.oy;
      positions[i3 + 2] = bz + s.oz;
    }
    geoRef.current.attributes.position.needsUpdate = true;
    restingRef.current = !active;
  });

  return (
    <group ref={groupRef} position={[0, 0, 0]}>
      <points material={material}>
        <bufferGeometry ref={geoRef}>
          <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
          <bufferAttribute attach="attributes-aColor"   count={count} array={colors}    itemSize={3} />
          <bufferAttribute attach="attributes-size"     count={count} array={sizes}     itemSize={1} />
        </bufferGeometry>
      </points>
    </group>
  );
}

export default function Experience() {
    return (
        <>
            <SunLight />
            <StarField count={QUALITY.starCount} />
            <GalaxyField count={QUALITY.galaxyCount} />
            <Swarm />
            <MouseTracker />
            <Model />
            <MotionBlurRenderer />
        </>
    );
}

// ─── GPU Temporal Accumulation Motion Blur ───────────────────────────────
// outputColorSpace is NEVER changed. Three.js's own materials (lineBasicMaterial,
// MeshStandardMaterial) carry <colorspace_fragment> and so write linear values to
// a render target and sRGB to the canvas, which the blit's linearToSRGB completes
// correctly. Custom ShaderMaterials get no such chunk and write their values
// verbatim either way — see outEncode() above, which gives them the matching
// encode on the path where this blit is not running.
// RawShaderMaterial — Three.js injects NO chunks at all, so we own the full
// pipeline here: RTs store linear values, the blit encodes to sRGB once.
const MBLUR_VERT = /* glsl */`
  precision highp float;
  attribute vec3 position;
  attribute vec2 uv;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;
// Blend: linear mix of two linear-stored frames — result stays linear
const MBLUR_BLEND_FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D tCurrent;
  uniform sampler2D tHistory;
  uniform float     uBlend;
  varying vec2 vUv;
  void main() {
    gl_FragColor = mix(texture2D(tCurrent, vUv), texture2D(tHistory, vUv), uBlend);
  }
`;
// Three.js r158 source (WebGLRenderer.js line ~1741):
//   const colorSpace = (_currentRenderTarget === null)
//       ? _this.outputColorSpace       // canvas → SRGBColorSpace → materials encode sRGB
//       : LinearSRGBColorSpace;         // ANY RT  → materials output LINEAR always
//
// So RT textures always store LINEAR values.  The browser treats canvas bytes as sRGB
// and applies sRGB-decode on display — linear values therefore look too dark.
// The blit MUST encode linearToSRGB so the value round-trips correctly.
// We NEVER use the direct render fallback — keeping materials always compiled for
// one colorSpace avoids shader recompilation glitches on transition.
const MBLUR_BLIT_FRAG = /* glsl */`
  precision highp float;
  uniform sampler2D tMap;
  uniform vec2      uRGBShift;
  varying vec2 vUv;
  vec3 linearToSRGB(vec3 c) {
    return mix(c * 12.92,
               1.055 * pow(clamp(c, 0.0, 1.0), vec3(1.0 / 2.2)) - 0.055,
               step(vec3(0.0031308), c));
  }
  void main() {
    // Radial split — R pushed out from center, B pulled in
    vec2 dir = (vUv - vec2(0.5)) * uRGBShift.x;
    float r = texture2D(tMap, vUv + dir).r;
    float g = texture2D(tMap, vUv).g;
    float b = texture2D(tMap, vUv - dir).b;
    float a = texture2D(tMap, vUv).a;
    gl_FragColor = vec4(linearToSRGB(vec3(r, g, b)), a);
  }
`;

function MotionBlurRendererInner() {
    const { gl, scene, camera, size } = useThree();

    // RTs store values exactly as Three.js would write to screen — sRGB-encoded
    // by the materials' colorspace_fragment shader chunk. The RawShaderMaterial
    // blit copies them verbatim; no re-encoding occurs because Three.js r158 does
    // the linear→sRGB step in the shader, not via hardware canvas sRGB.
    const rtScene = useMemo(() => new THREE.WebGLRenderTarget(size.width, size.height, { depthBuffer: true  }), []);
    const rtA     = useMemo(() => new THREE.WebGLRenderTarget(size.width, size.height, { depthBuffer: false }), []);
    const rtB     = useMemo(() => new THREE.WebGLRenderTarget(size.width, size.height, { depthBuffer: false }), []);

    const blendMat = useMemo(() => new THREE.RawShaderMaterial({
        vertexShader: MBLUR_VERT, fragmentShader: MBLUR_BLEND_FRAG,
        uniforms: { tCurrent: { value: null }, tHistory: { value: null }, uBlend: { value: 0 } },
        depthWrite: false, depthTest: false,
    }), []);
    const blitMat = useMemo(() => new THREE.RawShaderMaterial({
        vertexShader: MBLUR_VERT, fragmentShader: MBLUR_BLIT_FRAG,
        uniforms: { tMap: { value: null }, uRGBShift: { value: new THREE.Vector2(0, 0) } },
        depthWrite: false, depthTest: false,
    }), []);
    const rgbShiftRef = useRef(0);

    const { blendScene, blitScene, quadCam } = useMemo(() => {
        const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
        const mkScene = (mat) => {
            const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
            mesh.frustumCulled = false;
            const sc = new THREE.Scene(); sc.add(mesh); return sc;
        };
        return { blendScene: mkScene(blendMat), blitScene: mkScene(blitMat), quadCam: cam };
    }, []);

    useEffect(() => {
        [rtScene, rtA, rtB].forEach(rt => rt.setSize(size.width, size.height));
    }, [size]);

    const blurStrength = useRef(0);
    const pingRef      = useRef(true);
    const wasBlurring  = useRef(false);

    useFrame(() => {
        const mom    = dragState.dragMomentum;
        const target = Math.min(mom * 55, 0.78);
        const rate   = target > blurStrength.current ? 0.55 : 0.08;
        blurStrength.current += (target - blurStrength.current) * rate;
        const blend = blurStrength.current > 0.03 ? blurStrength.current : 0.0;

        // RGB radial split — strength tracks drag momentum
        const shiftTarget = Math.min(mom * 0.06, 0.018);
        rgbShiftRef.current += (shiftTarget - rgbShiftRef.current) * (shiftTarget > rgbShiftRef.current ? 0.55 : 0.06);
        blitMat.uniforms.uRGBShift.value.set(rgbShiftRef.current, 0);

        const prevClear = gl.autoClear;
        gl.autoClear = true;

        // ALWAYS render through RT → blit (never direct to canvas).
        // This keeps materials always compiled for LinearSRGBColorSpace (which
        // Three.js r158 uses internally for all RT renders per WebGLRenderer.js ~L1741)
        // and avoids any shader recompilation flicker on blur on/off transitions.

        // 1. Render scene → rtScene  (Three.js uses LinearSRGBColorSpace for RTs: linear stored)
        gl.setRenderTarget(rtScene);
        gl.render(scene, camera);

        if (blend === 0.0) {
            // No blur — blit rtScene straight to screen with linearToSRGB encode
            wasBlurring.current = false;
            blitMat.uniforms.tMap.value = rtScene.texture;
            gl.setRenderTarget(null);
            gl.render(blitScene, quadCam);
            gl.autoClear = prevClear;
            return;
        }

        const ping    = pingRef.current;
        const rtWrite = ping ? rtA : rtB;
        const rtRead  = ping ? rtB : rtA;
        pingRef.current = !ping;

        // 2. Pre-warm: first blur frame — fill both history buffers with current
        //    scene so we never blend against a black-initialised RT.
        if (!wasBlurring.current) {
            wasBlurring.current = true;
            blendMat.uniforms.tCurrent.value = rtScene.texture;
            blendMat.uniforms.tHistory.value = rtScene.texture;
            blendMat.uniforms.uBlend.value   = 0.0;
            gl.setRenderTarget(rtA); gl.render(blendScene, quadCam);
            gl.setRenderTarget(rtB); gl.render(blendScene, quadCam);
        }

        // 3. Blend rtScene + history → rtWrite
        blendMat.uniforms.tCurrent.value = rtScene.texture;
        blendMat.uniforms.tHistory.value = rtRead.texture;
        blendMat.uniforms.uBlend.value   = blend;
        gl.setRenderTarget(rtWrite);
        gl.render(blendScene, quadCam);

        // 4. Blit rtWrite → screen
        blitMat.uniforms.tMap.value = rtWrite.texture;
        gl.setRenderTarget(null);
        gl.render(blitScene, quadCam);

        gl.autoClear = prevClear;
    }, 1);

    return null;
}

// Wrapper: only mount the inner renderer where the extra passes are affordable.
// This path always costs a full-res offscreen scene render plus a fullscreen blit
// — even at zero blur — so mobile and low-tier devices skip it and render via
// R3F's default loop instead, at no overhead and with no RGB scatter.
// Draws the scene when the motion blur path is off.
//
// This is NOT redundant with R3F's own render: R3F only auto-renders while every
// useFrame subscriber is at priority 0 (`if (!state.internal.priority) gl.render(...)`).
// CursorSync runs at priority 100 so that it can overwrite CameraControls' cursor
// writes, and that alone hands the render loop over to us for the whole app. With
// no renderer mounted, nothing draws and the canvas stays black — which is exactly
// what happened on every device that skipped the blur.
//
// Priority 1 matches MotionBlurRendererInner: after all the priority-0 animation
// work, before CursorSync.
function PlainRenderer() {
    useFrame(({ gl, scene, camera }) => {
        gl.render(scene, camera);
    }, 1);
    return null;
}

function MotionBlurRenderer() {
    const { motionBlur } = useRenderPath();
    return motionBlur ? <MotionBlurRendererInner /> : <PlainRenderer />;
}

